/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
// components/control-panel.js
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PageType from 'linced-app/enums/page-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { dropTask } from 'ember-concurrency';
import moment from 'moment/moment';
import { tagName } from '@ember-decorators/component';

@tagName('')
export default class TeachingCycle extends Component {
  @service
  controlPanel;

  @service
  groupFilter;

  @service
  groupYears;

  @service
  notifications;

  @service
  personOptions;

  @service
  router;

  @service
  store;

  @service
  strings;

  @service
  students;

  PageType = PageType;
  PersonType = PersonType;
  @tracked groupSelectorYear = moment().year();
  @tracked currentYear = moment().year();
  @tracked groupSelectorOpen = false;

  // topicSubjectsTask loads the topic subjects when the plan panel opens.
  topicSubjectsTask = dropTask(async () => {
    return await this.store.query('goals/subject', { isTopic: true });
  });

  get topicSubjects() {
    return this.topicSubjectsTask.lastSuccessful?.value ?? [];
  }

  @computed
  get schoolPages() {
    return this.store
      .peekAll('posts/page')
      .filter((page) => page.type === PageType.SCHOOL)
      .filter((page) => page.visibleInMenu === true);
  }

  @computed
  get assessmentPages() {
    return this.store
      .peekAll('posts/page')
      .filter((page) => {
        const ids = page.get('assessment_ids');
        return ids && ids.length > 0;
      })
      .filter((page) => page.visibleInMenu === true);
  }

  get groupSelectorClass() {
    let classes = [];
    if (this.groupSelectorOpen) {
      classes.push('active');
    }
    if (this.groupSelectorYear !== this.currentYear) {
      classes.push('non-current-year');
    }
    return classes.join(' ');
  }

  @computed(
    'groupSelectorYear',
    'currentYear',
    'groupSelectorOpen',
    'numGroupChanges',
    'groupYears.loaded',
  )
  get groups() {
    if (!this.groupSelectorOpen) {
      // Early return so we don't risk fetching data when closed.
      return [];
    }
    return this.groupYears.groupsFor(this.groupSelectorYear, this.currentYear);
  }

  @computed('students.all.[]')
  get people() {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.get('students.all');
    return this.store.peekAll('person');
  }

  @computed('person.options.controlPanelPreferences')
  get panelPreferences() {
    return this.personOptions.getControlPanelPreferences(this.person);
  }

  // Computed property to get the active panel item
  @computed('linkType', 'controlPanel.formattedDescriptions.[]')
  get currentPanelItem() {
    const linkType = this.linkType;
    if (!linkType) return null;

    // Get the processed item from formattedDescriptions instead of the raw item
    const formattedItems = this.get('controlPanel.formattedDescriptions');
    return formattedItems.find(
      (item) => item.actionParams && item.actionParams[0] === linkType,
    );
  }

  @tracked
  editingPost = false;
  @tracked
  postBeingEdited = null;
  @tracked
  navDialog = false;
  @tracked
  subPanel = false;
  @tracked linkType = '';
  @tracked
  postType = 2;

  // Max number of notifications to show.
  // 10 for post notifications and 5 for other notifications.
  numNotificationsToShow = 15;

  @computed('notifications.totalUnread', 'numToShow')
  get unreadNotificationsLabel() {
    if (this.notifications.totalUnread >= this.numNotificationsToShow) {
      return `${this.numNotificationsToShow}+`;
    }
    return this.notifications.totalUnread;
  }

  onRouteTransition() {}

  // Cards carry their handler name as data (`newPost`, `goToRoute`,
  // `openSubPanel`). `{{action item.action}}` resolved that name at runtime;
  // `{{fn}}` needs a real function, so dispatch here.
  @action
  runCardAction(item) {
    let handler = this[item.action];
    if (typeof handler === 'function') {
      handler.call(this, item.actionParams);
    }
  }

  @action
  goToRoute([route, params = []]) {
    this.onRouteTransition();

    if (!isEmpty(params)) {
      this.router.transitionTo(route, ...params);
    } else {
      this.router.transitionTo(route);
    }
  }

  @action
  openSubPanel([type, loadGroups]) {
    this.subPanel = true;
    this.linkType = type;
    if (type === 'plan' && !this.topicSubjectsTask.lastSuccessful) {
      this.topicSubjectsTask.perform();
    }

    if (loadGroups) {
      this.groupSelectorOpen = true;
    }
    document
      .getElementById('main-content-container')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  @action
  closeSubPanel(type) {
    this.subPanel = false;
    this.linkType = type;
  }

  @action
  openDialog(type) {
    this.navDialog = true;
    this.linkType = type;
  }

  @action
  closeDialog() {
    this.navDialog = false;
    this.linkType = '';
  }

  @action
  newPost([type]) {
    this.postType = type;
    this.postBeingEdited = null;
    this.editingPost = true;
  }

  @action
  closePostDialogue() {
    this.editingPost = false;
  }

  @action
  closeEditPost() {
    // Implementation can be added here if needed
  }

  @action
  togglePanelPreference(index, field) {
    if (isEmpty(field)) {
      // Remove the object associated with the field
      this.updateCustomFieldObjectAtIndex({}, index);
      // Remove the attribute associated with the field
      this.clearApplicationsCustomFields(
        this.applications,
        `customField${index + 1}`,
      );
      // Save the preferred fields for the user's view
      this.personOptions.setEnrolmentApplicationsFields(
        this.selectedCustomFields,
      );
      return;
    }
    // There is a little bit of reverse-engineering going on,
    // as PaperSelect is useless at telling us which groupName we've got the
    // option from
    this.allSections.forEach((section) => {
      if (section.fields.includes(field)) {
        this.updateCustomFieldObjectAtIndex({ section, field }, index);
      }
    });
    // Save the preferred fields for the user's view
    this.personOptions.setEnrolmentApplicationsFields(
      this.selectedCustomFields,
    );
  }
}
